import React, { useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { FaDownload, FaChevronRight } from 'react-icons/fa'
import {
  formatEur, formatRange, computePricePositions, computeOpportunities, deduplicatedSavings, generateActionPlan, generateExpandedStepData,
} from './networkUtils'
import OppViz from './OppVisualizations'
import { exportOpportunitiesXlsx } from './oppExportXlsx'
import { generateDataPack } from './oppDataPack'
import AtlasPartDetailPanel from './AtlasPartDetailPanel'

const API_BASE = process.env.REACT_APP_BE_MAIN_URL

/** Call backend to generate a PDF playbook and trigger download */
async function downloadPlaybookPDF(opp, setLoading) {
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const d = opp.vizData || {}
  const items = (d.items || []).map(i => ({
    pn: i.pn, desc: i.desc || '', uc: i.uc, bm: i.bm, pp: i.pp,
    spend: i.spend, eau: i.eau || 0,
    file_id: i.file_id || (() => {
      const p = (opp.parts || []).find(pt => pt.pn === i.pn)
      return p?.file_id || null
    })(),
    material: i.material || '', method: i.method || '',
    cohort_entries: (i.cohort_entries || []).slice(0, 6),
    cohort_name: i.cohort_name || '',
    cohort_size: i.cohort_size || 0,
  }))
  const goodItems = (d.goodItems || []).map(g => ({
    pn: g.pn, desc: g.desc || '', uc: g.uc, bm: g.bm, pp: g.pp,
    spend: g.spend, eau: g.eau || 0, file_id: g.file_id || null,
    material: g.material || '', method: g.method || '',
    cohort_entries: (g.cohort_entries || []).slice(0, 6),
    cohort_name: g.cohort_name || '',
    cohort_size: g.cohort_size || 0,
  }))

  const body = {
    supplierName: d.supplierName || '',
    items,
    goodItems,
    strategies: opp.strategies || [],
    spendExposed: opp.spendExposed || 0,
    avgPP: opp.avgPP || 0,
    estSavings: opp.estSavings || [0, 0],
    negotiationTarget: opp._negotiationTarget || 1.1,
    // PN→file_id map for resolving cohort peer drawings
    pnFileMap: Object.fromEntries(
      (opp.parts || []).filter(p => p.file_id).map(p => [p.pn, p.file_id])
    ),
  }

  try {
    const res = await fetch(`${API_BASE}/ocr/network/playbook/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const txt = await res.text()
      alert(`Playbook generation failed: ${txt}`)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'Playbook.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(`Playbook generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

/** Call backend to generate a supplier-facing RFQ package and trigger download */
async function downloadRFQPackage(opp, setLoading) {
  setLoading(true)
  const token = sessionStorage.getItem('userToken')
  const d = opp.vizData || {}
  const items = (d.items || []).map(i => ({
    pn: i.pn, desc: i.desc || '', uc: i.uc, bm: i.bm, pp: i.pp,
    spend: i.spend, eau: i.eau || 0,
    file_id: i.file_id || (() => {
      const p = (opp.parts || []).find(pt => pt.pn === i.pn)
      return p?.file_id || null
    })(),
    material: i.material || '', method: i.method || '',
    cohort_entries: (i.cohort_entries || []).slice(0, 20),
    cohort_name: i.cohort_name || '',
    cohort_size: i.cohort_size || 0,
  }))
  const goodItems = (d.goodItems || []).map(g => ({
    pn: g.pn, desc: g.desc || '', pp: g.pp, spend: g.spend, uc: g.uc, bm: g.bm,
    eau: g.eau || 0, file_id: g.file_id || null,
    material: g.material || '', method: g.method || '',
    cohort_entries: (g.cohort_entries || []).slice(0, 10),
    cohort_name: g.cohort_name || '',
    cohort_size: g.cohort_size || 0,
  }))

  const body = {
    supplierName: d.supplierName || '',
    fromName: 'Thermo Fisher Scientific',
    items,
    goodItems,
    spendExposed: opp.spendExposed || 0,
    pnFileMap: Object.fromEntries(
      (opp.parts || []).filter(p => p.file_id).map(p => [p.pn, p.file_id])
    ),
  }

  try {
    const res = await fetch(`${API_BASE}/ocr/network/rfq-package/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const txt = await res.text()
      alert(`RFQ generation failed: ${txt}`)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'RFQ_Package.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(`RFQ generation error: ${err.message}`)
  } finally {
    setLoading(false)
  }
}

const OPP_TYPES = {
  negotiation: { label: 'Negotiation', icon: '💰', color: '#dc2626', bg: '#fef2f2' },
  sole_source: { label: 'Sole-Source Risk', icon: '⚠️', color: '#d97706', bg: '#fffbeb' },
  part_family: { label: 'Part Family', icon: '🔗', color: '#6366f1', bg: '#eef2ff' },
  quick_win: { label: 'Quick Win', icon: '⚡', color: '#059669', bg: '#ecfdf5' },
  site_gap: { label: 'Site Price Gap', icon: '🌍', color: '#0891b2', bg: '#ecfeff' },
}

const STEP_STYLE = `
.opp-action-step .step-data{background:#eef2ff;color:#3730a3;font-weight:600;padding:0 5px;border-radius:4px;white-space:nowrap}
.opp-action-step strong{color:#1f2937}
`

function Donut({ segments, count }) {
  const R = 60, CX = 80, CY = 80, C = 2 * Math.PI * R
  let cum = 0
  return (
    <svg viewBox="0 0 160 160" className="w-[110px] h-[110px] flex-shrink-0">
      {segments.map((seg, i) => {
        const dashLen = C * seg.pct / 100
        const el = (
          <circle key={i} cx={CX} cy={CY} r={R} fill="none" stroke={seg.color} strokeWidth="18"
            strokeDasharray={`${dashLen} ${C - dashLen}`} strokeDashoffset={-C * cum / 100}
            style={{ transition: 'stroke-dashoffset 0.5s' }} />
        )
        cum += seg.pct
        return el
      })}
      <text x={CX} y={CY - 4} textAnchor="middle" fontSize="22" fontWeight="700" fill="#1f2937">{count}</text>
      <text x={CX} y={CY + 14} textAnchor="middle" fontSize="9" fill="#94a3b8">opportunities</text>
    </svg>
  )
}

function Formula({ children }) {
  return (
    <div className="font-mono text-[11px] bg-gray-50 text-gray-700 border border-gray-200 rounded-md px-3 py-2 my-1.5 overflow-x-auto whitespace-pre leading-relaxed">{children}</div>
  )
}

function Section({ n, title, children }) {
  return (
    <div className="border-t border-gray-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
      <div className="text-xs font-bold text-gray-800 mb-2">{n}. {title}</div>
      <div className="text-[11px] text-gray-600 leading-relaxed space-y-2">{children}</div>
    </div>
  )
}

const OPP_METHOD = [
  {
    icon: '💰', title: 'Negotiation', color: '#dc2626',
    trigger: 'A supplier’s line has Price Position > 1.3 and spend > €500. Lines are grouped by supplier; group must total > €500 overspend.',
    formula: 'overspend = spend × (1 − 1 / PP)',
    note: 'PP is the volume-normalized benchmark ratio (see §2). Grouping by supplier turns scattered lines into one negotiation with real leverage.',
  },
  {
    icon: '⚠️', title: 'Sole-Source Risk', color: '#d97706',
    trigger: 'Part has exactly 1 supplier and total spend > €2,000. Grouped by supplier; group must total > €5,000.',
    formula: 'savings ≈ 0.12 × total_spend',
    note: 'Without competitive tension, prices drift up. Qualifying a second source typically recovers 8–15%; we use a conservative 12%.',
  },
  {
    icon: '�', title: 'Part Family', color: '#6366f1',
    trigger: 'Engineering-similar parts (≥4 shared attributes: method, material, size, tolerance, complexity) that are either priced ≥30% apart OR fragmented across ≥4 suppliers. Combined savings must exceed €2,000.',
    formula: 'Price alignment: expectedᵢ = best_UC × (best_EAU / EAUᵢ)^0.4, savings = Σ overpriced.spend × (1 − expectedᵢ / UCᵢ)\nConsolidation: savings ≈ 0.08 × family_spend\nFinal savings = max(price_alignment, consolidation)',
    note: 'Combines three levers: similarity scoring (how similar are the parts really?), price alignment (why do similar parts cost different amounts?), and supplier consolidation (fragmented volume forfeits leverage). Parts with \u226570% similarity to the reference are strong candidates for price alignment; parts below 40% may have legitimate engineering differences.',
  },
  {
    icon: '⚡', title: 'Quick Win', color: '#059669',
    trigger: 'A part with ≥2 suppliers where one is expensive (PP > 1.5) and another is already cheap (PP < 1.0) — a qualified cheaper source exists.',
    formula: 'savings = worst.spend × (1 − best.PP / worst.PP)',
    note: 'No new qualification — just redirect the PO to the supplier already serving the part cheaper elsewhere.',
  },
]

function MethodologyPanel() {
  return (
    <details className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-gray-700 select-none">How does this work?</summary>
      <div className="px-5 pb-5 space-y-6">

        {/* Visual pipeline overview */}
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 p-5">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Data Pipeline</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">📄</div>
              <div className="text-[10px] font-semibold text-gray-700">CAD Drawings</div>
              <div className="text-[9px] text-gray-400">Process, material, size</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">🤖</div>
              <div className="text-[10px] font-semibold text-gray-700">AI Extraction</div>
              <div className="text-[9px] text-gray-400">Tolerances, GD&T, dims</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">💳</div>
              <div className="text-[10px] font-semibold text-gray-700">Purchase Data</div>
              <div className="text-[9px] text-gray-400">Spend, unit cost, EAU</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">🔗</div>
              <div className="text-[10px] font-semibold text-gray-700">Join & Cohort</div>
              <div className="text-[9px] text-gray-400">Group similar parts</div>
            </div>
            <div className="text-gray-300 text-lg">→</div>
            <div className="flex flex-col items-center px-3 py-2 bg-[#ecfdf5] rounded-lg border border-green-200 shadow-sm min-w-[90px]">
              <div className="text-xl mb-1">💡</div>
              <div className="text-[10px] font-semibold text-green-800">Opportunities</div>
              <div className="text-[9px] text-green-600">Ranked & scored</div>
            </div>
          </div>
        </div>

        {/* Section 1: Data Matching */}
        <Section n="1" title="How purchase data matches to drawings">
          <p>
            Spend rows reference bare part numbers (e.g. <span className="font-mono text-[11px]">1098833</span>) while CAD drawings carry
            extensions and revision tails (<span className="font-mono text-[11px]">1098833-D-110-01.pdf</span>). Both sides are reduced
            to a <strong>canonical part-number key</strong> and joined tenant-wide, independent of folder.
          </p>
          <p className="mt-2">
            The deterministic key matches ~59% of drawings; the remaining are resolved by an AI fallback that records confident matches
            so the join stays fast. Multiple spend rows for one part (across sites/suppliers) all attach to that drawing.
          </p>
        </Section>

        {/* Section 2: Price Benchmarking */}
        <Section n="2" title="Price benchmarking (volume-normalized)">
          <p>
            Parts are grouped into <strong>sourcing cohorts</strong> — clusters sharing manufacturing characteristics.
            The more attributes shared, the more meaningful the comparison:
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Cohort Attributes</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/></div>
                <span className="text-[11px] text-gray-700"><strong>4+ attributes</strong> (method + material + size + tolerance) — most precise</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-green-500"/><div className="w-2 h-2 rounded-sm bg-gray-200"/></div>
                <span className="text-[11px] text-gray-700"><strong>3 attributes</strong> — solid, executable comparison</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5"><div className="w-2 h-2 rounded-sm bg-amber-500"/><div className="w-2 h-2 rounded-sm bg-amber-500"/><div className="w-2 h-2 rounded-sm bg-gray-200"/><div className="w-2 h-2 rounded-sm bg-gray-200"/></div>
                <span className="text-[11px] text-gray-700"><strong>2 attributes</strong> — directional; validate with quotes</span>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 mt-2 italic">Parts with fewer than 2 known attributes are excluded from benchmarking.</div>
          </div>
          <p>Within each cohort (≥3 priced members) we compute a volume-adjusted benchmark:</p>
          <Formula>{`benchmark = cohort_median_UC × (cohort_median_EAU / part_EAU)^0.4
Price Position (PP) = unit_cost / benchmark
  PP 1.0 = at market    PP 1.5 = 50% above market`}</Formula>
          <p className="mt-2">
            The <span className="font-mono text-[11px]">^0.4</span> exponent is a volume/learning-curve adjustment —
            low-volume parts aren't falsely flagged as overpriced. A supplier's unit cost is the <strong>EAU-weighted blend</strong>
            of all rows for that part (Σ UC×EAU ÷ Σ EAU).
          </p>
        </Section>

        {/* Section 3: Savings Calculation */}
        <Section n="3" title="How savings are calculated">
          <p>
            Each opportunity type has its own savings formula. The <strong>Negotiation Target</strong> setting
            (adjustable in Opportunity Settings above) determines the price you aim to negotiate down to.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>💰</span>
                <span className="text-xs font-semibold text-red-600">Negotiation</span>
              </div>
              <Formula>{`savings = spend × (1 − target / PP)
Example: €10K spend at PP 1.5, target 1.1×
  = €10K × (1 − 1.1/1.5) = €2,667`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Only parts with PP above the <em>Price Threshold</em> setting are flagged.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>⚠️</span>
                <span className="text-xs font-semibold text-amber-600">Sole-Source Risk</span>
              </div>
              <Formula>{`savings = total_sole_spend × 12%`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Assumes qualifying a second source yields 8–15% improvement through competitive pressure. 12% midpoint.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>🔗</span>
                <span className="text-xs font-semibold text-indigo-600">Part Family</span>
              </div>
              <Formula>{`savings = Σ part_spend × (1 − best_UC / part_UC)
best_UC = cheapest in-family unit cost`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">Triggers when ≥4 similar parts are priced ≥30% apart OR fragmented across ≥4 suppliers.</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <span>⚡</span>
                <span className="text-xs font-semibold text-green-600">Quick Win</span>
              </div>
              <Formula>{`savings = spend × (1 − alt_PP / current_PP)
Where alt already qualified (PP < 1.0)`}</Formula>
              <div className="text-[10px] text-gray-500 mt-1">A part with ≥2 suppliers where one is expensive (PP &gt; 1.5) and another is cheap (PP &lt; 1.0).</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
            <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">Deduplication</div>
            <p className="text-[11px] text-amber-900">
              The same part+supplier can appear in multiple opportunity types. The headline "Potential Annual Savings" KPI
              only counts the <strong>highest savings claim</strong> for each part+supplier pair — never double-counts.
            </p>
          </div>
        </Section>

        {/* Section 4: Savings Display */}
        <Section n="4" title="Savings display & the Savings Estimate setting">
          <p>
            Raw modelled savings represent a theoretical maximum. Real negotiations rarely achieve 100%.
            The <strong>Savings Estimate</strong> slider applies a multiplier (default 65%) and shows a range:
          </p>
          <div className="bg-gray-50 rounded-lg p-3 my-3 text-[11px]">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-center"><div className="text-sm font-bold text-gray-700">€10,000</div><div className="text-[9px] text-gray-400">Raw model</div></div>
              <div className="text-gray-300 text-lg">→</div>
              <div className="text-center"><div className="text-sm font-bold text-gray-700">× 65%</div><div className="text-[9px] text-gray-400">Multiplier</div></div>
              <div className="text-gray-300 text-lg">→</div>
              <div className="text-center"><div className="text-sm font-bold text-green-600">€5K – €8K</div><div className="text-[9px] text-gray-400">Displayed range</div></div>
            </div>
          </div>
          <p>Adjusting up = more optimistic; adjusting down = safer for executive reporting.</p>
        </Section>

        {/* Section 5: Negotiation Target */}
        <Section n="5" title="Negotiation Target setting">
          <p>
            The <strong>Negotiation Target</strong> (default 1.1× = 10% premium over benchmark) defines the price level
            you aim to negotiate toward. It affects everything:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-[11px] mt-2">
            <li><strong>Savings calculations</strong> — higher target = less savings per part (you accept a larger premium as "fair")</li>
            <li><strong>Action Plans</strong> — target prices, evidence text, and challenge scripts update dynamically</li>
            <li><strong>PDF Playbook</strong> — sensitivity table, per-part targets, script language all reflect the setting</li>
            <li><strong>Data Pack</strong> — printable data pack uses this target for all calculations</li>
            <li><strong>Chart tooltips</strong> — overspend and target price on hover reflect this setting</li>
          </ul>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Practical guidance</div>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.0×</span><span className="text-gray-600">Exact benchmark — aggressive, use with strong leverage</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right font-semibold text-green-700">1.1×</span><span className="text-gray-600">Default — 10% premium for service/quality. Fair and defensible.</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.2×</span><span className="text-gray-600">Conservative — for strategic or hard-to-replace suppliers</span></div>
              <div className="flex items-center gap-2"><span className="font-mono w-8 text-right">1.5×+</span><span className="text-gray-600">Only flag extreme outliers — useful for data-quality investigation</span></div>
            </div>
          </div>
        </Section>

        {/* Section 6: Achievability */}
        <Section n="6" title="Achievability scoring">
          <p>Each opportunity receives an <strong>Achievability score</strong> (0–100) reflecting how actionable it is.</p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Scoring Factors</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Opportunity type</strong> — quick wins and part families score higher</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Cohort granularity</strong> — 4 attributes outscores 2</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Cohort depth</strong> — 10+ transactions outscores 3</span></div>
              <div className="flex items-start gap-1.5"><span className="text-green-600 font-bold">+</span> <span><strong>Line item count</strong> — 5+ parts give leverage</span></div>
              <div className="flex items-start gap-1.5"><span className="text-amber-600 font-bold">−</span> <span><strong>Outlier detection</strong> — PP &gt;3× flags potential data errors</span></div>
              <div className="flex items-start gap-1.5"><span className="text-red-600 font-bold">−</span> <span><strong>Execution complexity</strong> — new qualification reduces score</span></div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] flex-wrap">
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-green-500"/>)}</div><span><strong>High</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2,3].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-amber-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Medium</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1,2].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-red-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Low</strong></span></div>
            <div className="flex items-center gap-1"><div className="flex gap-0.5">{[1].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-400"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/><div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span><strong>Explore</strong></span></div>
          </div>
        </Section>

        {/* Section 7: Complexity Score */}
        <Section n="7" title="Manufacturing complexity score">
          <p>
            Each part receives a <strong>0–100 complexity score</strong> derived from 7 weighted axes of the CAD extraction.
            Higher complexity means harder to manufacture and fewer qualified suppliers — which affects achievability and cohort quality.
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">Scoring Axes (max 100 pts)</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-red-500 h-full rounded-full" style={{width:'100%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Feature density</strong> (20 pts) — holes, pockets, slots, undercuts, threads, bends, welds</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-red-500 h-full rounded-full" style={{width:'100%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Tolerance precision</strong> (20 pts) — tightest linear + GD&T tolerance band</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'75%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Axis / setup</strong> (15 pts) — 3/4/5-axis + number of setups required</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Material difficulty</strong> (10 pts) — Ti/Inco &gt; stainless &gt; steel &gt; aluminum &gt; plastic</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Specification density</strong> (10 pts) — dimension count + GD&T frames + surface finishes + notes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-amber-500 h-full rounded-full" style={{width:'50%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Certification burden</strong> (10 pts) — NADCAP, ITAR, AS9100 and other cert requirements</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-green-500 h-full rounded-full" style={{width:'25%'}}/></div>
                <span className="text-[11px] text-gray-700 flex-1"><strong>Size / BTF</strong> (5 pts) — envelope class + buy-to-fly waste ratio</span>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 mt-2 italic">+ Secondary processes bonus (up to +10 pts for heat treat, plating, NDT, etc.)</div>
          </div>
          <div className="flex items-center gap-4 text-[11px] flex-wrap mt-2">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-100 border border-green-300"/><span><strong>Simple</strong> (0–25)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"/><span><strong>Moderate</strong> (26–50)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-100 border border-red-300"/><span><strong>Complex</strong> (51–75)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-100 border border-purple-300"/><span><strong>Very Complex</strong> (76–100)</span></div>
          </div>
          <p className="mt-2">
            Complexity feeds into <strong>cohort grouping</strong> (parts are benchmarked against peers of similar complexity),
            <strong>achievability scoring</strong> (complex parts are harder to re-source), and <strong>Part Family matching</strong>
            (the Complexity Tier attribute contributes 2 of 20 similarity points).
          </p>
        </Section>

        {/* Section 8: Data Quality */}
        <Section n="8" title="Data-quality rules">
          <ul className="list-disc pl-4 space-y-1.5 text-[11px]">
            <li><strong>Inforecord / price-reference rows excluded</strong> — zero-spend quoted list prices are not purchases.</li>
            <li><strong>EAU required for price opportunities.</strong> No volume = no PP = skipped by Negotiation/Quick-Win.</li>
            <li><strong>Outlier thresholds:</strong> PP &gt;3× → warning; &gt;5× → likely UoM mismatch; &gt;10× → data error.</li>
            <li><strong>Cross-site outlier removal</strong> — per-site prices &gt;8× the part median are excluded.</li>
            <li><strong>Spend-weighted ranking</strong> — high-value parts surface first.</li>
          </ul>
        </Section>

        {/* Section 9: Part Families */}
        <Section n="9" title="Part Family similarity analysis">
          <p>
            Each part is scored against the <strong>best-priced reference</strong> using a multi-attribute similarity function (max 20 pts):
          </p>
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 my-3">
            <div className="grid grid-cols-2 gap-1.5 text-[11px]">
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">4</span> <span>Size band</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Material family</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Primary process</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-green-500 text-[8px] text-white text-center font-bold leading-3">3</span> <span>Dimensional proximity</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Tolerance band</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Complexity tier</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-500 text-[8px] text-white text-center font-bold leading-3">2</span> <span>Envelope class</span></div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-gray-400 text-[8px] text-white text-center font-bold leading-3">1</span> <span>Machining route</span></div>
            </div>
          </div>
          <div className="space-y-1 text-[11px]">
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-green-600"/>{'≥'}70% — Strong match. Price differences are negotiable.</div>
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-amber-500"/>40–69% — Moderate. Investigate before negotiating.</div>
            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500"/>&lt;40% — Weak. Gap may reflect real engineering differences.</div>
          </div>
        </Section>

        {/* Section 10: Outputs */}
        <Section n="10" title="PDF Playbook & Data Pack">
          <p>Each opportunity includes actionable outputs:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-gray-700 mb-1">📕 PDF Playbook <span className="text-[9px] text-gray-400 font-normal">(Negotiation only)</span></div>
              <ul className="list-disc pl-3 space-y-0.5 text-[10px] text-gray-600">
                <li>Executive summary with KPIs</li>
                <li>Strategy scoring & recommendation</li>
                <li>Sensitivity table (savings at different targets)</li>
                <li>Per-part negotiation briefs with evidence + scripts</li>
                <li>CAD drawing images for each part</li>
                <li>Cohort peer comparison cards</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-gray-700 mb-1">📄 Data Pack <span className="text-[9px] text-gray-400 font-normal">(All types)</span></div>
              <ul className="list-disc pl-3 space-y-0.5 text-[10px] text-gray-600">
                <li>Line-item detail (UC, BM, PP, target, savings)</li>
                <li>Engineering equivalence (Part Family)</li>
                <li>Supplier landscape analysis</li>
                <li>Sensitivity analysis table</li>
                <li>Per-part cohort evidence</li>
                <li>Draft supplier email</li>
              </ul>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            Both outputs use the current <strong>Negotiation Target</strong> setting — change it and regenerated documents reflect the new target.
          </p>
        </Section>
      </div>
    </details>
  )
}


const TUTORIAL_STEPS = [
  {
    title: 'Welcome to Savings Opportunities',
    icon: '👋',
    content: 'This page shows algorithmically-identified cost reduction opportunities across your purchased parts. Each opportunity is backed by data — prices, volumes, and engineering attributes extracted from your drawings and purchase history.',
    visual: (
      <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 mt-2">
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">📄</div><div className="text-[10px] text-gray-500">CAD Drawings</div></div>
        <div className="text-gray-300 text-lg">+</div>
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">💳</div><div className="text-[10px] text-gray-500">Purchase History</div></div>
        <div className="text-gray-300 text-lg">=</div>
        <div className="flex-1 text-center p-2"><div className="text-2xl mb-1">💡</div><div className="text-[10px] text-gray-500">Opportunities</div></div>
      </div>
    ),
  },
  {
    title: 'Opportunity Types',
    icon: '📊',
    content: 'There are 4 types of opportunities, each with a different mechanism:',
    visual: (
      <div className="grid grid-cols-1 gap-1.5 mt-2">
        <div className="flex items-center gap-2 bg-green-50 rounded px-2 py-1.5"><span>⚡</span><span className="text-[11px]"><strong>Quick Win</strong> — cheaper qualified supplier already exists. Just switch the PO.</span></div>
        <div className="flex items-center gap-2 bg-red-50 rounded px-2 py-1.5"><span>💰</span><span className="text-[11px]"><strong>Negotiation</strong> — supplier charges above the market benchmark. Negotiate with data.</span></div>
        <div className="flex items-center gap-2 bg-indigo-50 rounded px-2 py-1.5"><span>🔗</span><span className="text-[11px]"><strong>Part Family</strong> — engineering-similar parts with price spread or supplier fragmentation. Align pricing and consolidate.</span></div>
        <div className="flex items-center gap-2 bg-amber-50 rounded px-2 py-1.5"><span>⚠️</span><span className="text-[11px]"><strong>Sole-Source Risk</strong> — only one supplier. Qualify a second to enable competition.</span></div>
      </div>
    ),
  },
  {
    title: 'Achievability Rating',
    icon: '🎯',
    content: 'Not all opportunities are equally easy to capture. The Achievability column tells you how actionable each one is:',
    visual: (
      <div className="space-y-2 mt-2">
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-green-500"/>)}</div><span className="text-[11px]"><strong>High</strong> — can execute immediately (e.g. redirect a PO to a cheaper site)</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2,3].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-amber-500"/>)}<div className="w-1.5 h-3 rounded-sm bg-gray-200"/></div><span className="text-[11px]"><strong>Medium</strong> — requires a negotiation or quote request</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5">{[1,2].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-red-500"/>)}{[3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-200"/>)}</div><span className="text-[11px]"><strong>Low</strong> — coarse benchmark; validate with quotes before acting</span></div>
        <div className="flex items-center gap-3"><div className="flex gap-0.5"><div className="w-1.5 h-3 rounded-sm bg-gray-400"/>{[2,3,4].map(i=><div key={i} className="w-1.5 h-3 rounded-sm bg-gray-200"/>)}</div><span className="text-[11px]"><strong>Exploratory</strong> — directional signal; needs further investigation</span></div>
      </div>
    ),
  },
  {
    title: 'Reading an Opportunity',
    icon: '🔍',
    content: 'Click any row to expand it. Inside you will find:',
    visual: (
      <div className="space-y-1.5 mt-2 text-[11px]">
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">1.</span> A plain-English explanation of what was found and why it matters</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">2.</span> A visual chart showing the price distribution or gap — hover any bar for details, click a part label to open its record</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">3.</span> The affected parts with similarity scores (Part Families) — showing how comparable each part is to the reference</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">4.</span> A step-by-step Action Plan with similarity intelligence — tells you which parts are safe to negotiate hard</div>
        <div className="flex items-start gap-2"><span className="text-green-600 font-bold">5.</span> A "Generate Data Pack" button that creates a print-ready brief with engineering equivalence analysis</div>
      </div>
    ),
  },
  {
    title: 'Taking Action',
    icon: '🚀',
    content: 'Start with High-Achievability opportunities at the top. For each one:',
    visual: (
      <div className="space-y-1.5 mt-2 text-[11px]">
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Expand the opportunity and review the Action Plan — it includes similarity-backed recommendations</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Click any part label in the chart to inspect its engineering record and see similar parts</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Click "Generate Data Pack" for a professional brief with engineering equivalence and price targets</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Send the data pack to your supplier — it contains ready-to-use negotiation language</div>
        <div className="flex items-start gap-2 bg-green-50 rounded px-2 py-1.5"><span>✅</span> Use "Export Excel" to download all opportunities for your team</div>
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800">
          💡 Tip: Parts with similarity scores below 40% may have legitimate engineering differences — validate before demanding price alignment.
        </div>
      </div>
    ),
  },
]

function TutorialOverlay({ onClose }) {
  const [step, setStep] = useState(0)
  const s = TUTORIAL_STEPS[step]
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Progress */}
        <div className="flex gap-1 px-6 pt-5">
          {TUTORIAL_STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full ${i <= step ? 'bg-[#1e5045]' : 'bg-gray-200'}`} />
          ))}
        </div>
        {/* Content */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{s.icon}</span>
            <h3 className="text-base font-bold text-gray-800">{s.title}</h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{s.content}</p>
          {s.visual}
        </div>
        {/* Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700">Skip tutorial</button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100">Back</button>
            )}
            {step < TUTORIAL_STEPS.length - 1 ? (
              <button onClick={() => setStep(step + 1)} className="px-4 py-1.5 text-xs font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57]">Next</button>
            ) : (
              <button onClick={onClose} className="px-4 py-1.5 text-xs font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57]">Get started</button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

function AchievabilityBadge({ achievability }) {
  const [showTip, setShowTip] = React.useState(false)
  const [tipStyle, setTipStyle] = React.useState({})
  const badgeRef = React.useRef(null)
  const tipRef = React.useRef(null)
  const hideTimer = React.useRef(null)

  if (!achievability) return null

  const colorMap = { green: 'bg-green-100 text-green-800 border-green-200', amber: 'bg-amber-100 text-amber-800 border-amber-200', red: 'bg-red-100 text-red-800 border-red-200', gray: 'bg-gray-100 text-gray-600 border-gray-200' }
  const barColor = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', gray: 'bg-gray-400' }

  const positionTip = () => {
    if (!badgeRef.current) return
    const rect = badgeRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 200
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - 140, window.innerWidth - 300))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 8, left, zIndex: 9999 })
    }
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setShowTip(false), 150) }

  const handleMouseEnter = () => { cancelHide(); positionTip(); setShowTip(true) }
  const handleMouseLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  return (
    <div className="relative inline-block">
      <div ref={badgeRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full border cursor-help ${colorMap[achievability.color]}`}>
        <div className="flex gap-0.5">
          {[1,2,3,4].map(i => (
            <div key={i} className={`w-1 h-2.5 rounded-sm ${i <= Math.ceil(achievability.score / 25) ? barColor[achievability.color] : 'bg-gray-200'}`} />
          ))}
        </div>
        {achievability.label}
      </div>
      {showTip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          onMouseEnter={handleTipEnter}
          onMouseLeave={handleTipLeave}
          style={tipStyle}
          className="w-[280px] bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-4 py-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-800">Achievability</span>
            <span className={`text-xs font-bold ${achievability.color === 'green' ? 'text-green-700' : achievability.color === 'amber' ? 'text-amber-700' : achievability.color === 'red' ? 'text-red-700' : 'text-gray-500'}`}>{achievability.score}/100</span>
          </div>
          <p className="text-[11px] text-gray-600 leading-relaxed">{achievability.tip}</p>
          {achievability.warnings.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              {achievability.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700">
                  <span className="shrink-0 mt-0.5">&#9888;</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

function buildPPClipboardText(s, part) {
  const pp = s.price_position
  if (pp == null || !s.benchmark) return ''
  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdict = pp > 1.5 ? 'Above expected — negotiation target' : pp < 0.7 ? 'Well below expected — good deal' : 'Within normal range'
  let header = ''
  if (part) {
    header = `Part: ${part.pn || '—'}${part.description ? ' — ' + part.description : ''}\n`
  }
  let formula = ''
  if (s.cohort_median_uc && s.cohort_median_eau && eau > 0) {
    formula = `\nFormula: Expected = Cohort Median UC × (Cohort Median EAU / Part EAU)^0.4\n       = €${s.cohort_median_uc.toFixed(2)} × (${s.cohort_median_eau.toLocaleString()} / ${eau.toLocaleString()})^0.4\n       = €${s.benchmark.toFixed(2)}`
  }
  let text = `${header}Supplier: ${s.name}\nActual: €${Number(s.unit_cost).toFixed(2)}\nExpected: €${s.benchmark.toFixed(2)}${eau > 0 ? ` (EAU ${eau.toLocaleString()})` : ''}\nPosition: ${pct}% ${direction} benchmark\n${verdict}${formula}`
  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost)
  if (entries.length > 0) {
    const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')
    text += `\n\nCohort: ${cohortLabel} (${s.cohort_size || entries.length} transactions from parts with same process family/method/material/size)\nMedian UC: €${s.cohort_median_uc?.toFixed(2) || '?'}, Median EAU: ${s.cohort_median_eau?.toLocaleString() || '?'}`
    text += `\nPart #\tDescription\tSupplier\tUC\tEAU`
    entries.forEach(e => {
      text += `\n${e.pn || '—'}\t${e.description || '—'}\t${e.supplier}\t€${e.unit_cost.toFixed(2)}\t${e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}`
    })
  }
  return text
}

function PPTooltip({ s, part }) {
  const pp = s.price_position
  if (pp == null && !s.benchmark) {
    return <div className="text-[11px] text-gray-500">No price position — EAU data missing, cannot compute benchmark.</div>
  }
  if (pp == null) return null

  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdictColor = pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-700' : 'text-gray-600'
  const verdict = pp > 1.5 ? '⚠ Above expected — negotiation target' : pp < 0.7 ? '✓ Well below expected — good deal' : '○ Within normal range'

  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost).slice(0, 10)
  const hasFormula = s.cohort_median_uc && s.cohort_median_eau && eau > 0
  const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')

  return (
    <div>
      {part && (
        <div className="text-[10px] text-gray-500 mb-1 truncate">
          <span className="font-mono font-medium text-gray-700">{part.pn}</span>
          {part.description && <span className="ml-1.5">{part.description}</span>}
        </div>
      )}
      <div className={`flex ${entries.length > 0 ? 'gap-4' : ''}`}>
        {/* Left: Price position details */}
        <div className="shrink-0">
          <div className="font-semibold text-[11px] text-gray-900 truncate mb-1">{s.name}</div>
          <table className="text-[10px]">
            <tbody>
              <tr><td className="text-gray-500 pr-2 py-0.5">Actual</td><td className="font-mono text-gray-900">€{Number(s.unit_cost).toFixed(2)}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Expected</td><td className="font-mono text-gray-900">€{s.benchmark.toFixed(2)}{eau > 0 ? <span className="text-gray-400 ml-1">(EAU {eau.toLocaleString()})</span> : ''}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Position</td><td className={`font-semibold ${verdictColor}`}>{pct}% {direction}</td></tr>
            </tbody>
          </table>
          <div className={`text-[10px] font-medium mt-1 ${verdictColor}`}>{verdict}</div>
          {hasFormula && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-[9px] text-gray-500 font-mono leading-relaxed">
              <div>Expected = Median UC × (Median EAU / Part EAU)<sup>0.4</sup></div>
              <div className="text-gray-400">= €{s.cohort_median_uc.toFixed(2)} × ({s.cohort_median_eau.toLocaleString()} / {eau.toLocaleString()})<sup>0.4</sup> = €{s.benchmark.toFixed(2)}</div>
            </div>
          )}
        </div>
        {/* Right: Cohort table */}
        {entries.length > 0 && (
          <div className="border-l border-gray-200 pl-3 min-w-0 overflow-x-auto">
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {cohortLabel} ({s.cohort_size || entries.length})
              {s.cohort_median_uc && <span className="font-normal normal-case ml-1">· median €{s.cohort_median_uc.toFixed(2)}</span>}
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium pb-0.5 pr-2">Part</th>
                  <th className="text-left font-medium pb-0.5 pr-2">Supplier</th>
                  <th className="text-right font-medium pb-0.5 pr-2">UC</th>
                  <th className="text-right font-medium pb-0.5">EAU</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className={e.supplier === s.name ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                    <td className="pr-2 py-0.5 truncate max-w-[140px] font-mono" title={`${e.pn} — ${e.description || ''}`}>{e.pn?.slice(0, 14) || '—'}{e.description ? <span className="font-sans text-gray-400 ml-1 text-[9px]">{e.description.slice(0, 12)}</span> : ''}</td>
                    <td className="pr-2 py-0.5 truncate max-w-[120px]">{e.supplier.slice(0, 18)}{e.supplier.length > 18 ? '…' : ''}</td>
                    <td className="text-right pr-2 py-0.5 font-mono whitespace-nowrap">€{e.unit_cost.toFixed(2)}</td>
                    <td className="text-right py-0.5 font-mono whitespace-nowrap">{e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(s.cohort_entries || []).length > 10 && (
              <div className="text-[9px] text-gray-400 mt-0.5">+{s.cohort_entries.length - 10} more</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SupplierChip({ s, multi, part }) {
  const [copied, setCopied] = React.useState(false)
  const [showTip, setShowTip] = React.useState(false)
  const [tipStyle, setTipStyle] = React.useState({})
  const chipRef = React.useRef(null)
  const tipRef = React.useRef(null)
  const hideTimer = React.useRef(null)
  const pp = s.price_position
  const ppColor = pp == null ? 'text-gray-400' : pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-600' : 'text-gray-500'
  const uc = s.unit_cost > 0 ? `€${Number(s.unit_cost).toFixed(2)}` : '—'
  const hasTip = pp != null || !s.benchmark

  const positionTip = () => {
    if (!chipRef.current) return
    const rect = chipRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 350
    const tipW = 680 // max-w of tooltip
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 12))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left, maxWidth: window.innerWidth - 24, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 8, left, maxWidth: window.innerWidth - 24, zIndex: 9999 })
    }
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setShowTip(false), 150) }

  const handleMouseEnter = () => {
    cancelHide()
    positionTip()
    setShowTip(true)
  }

  const handleMouseLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  const handleCopy = (e) => {
    e.stopPropagation()
    const text = buildPPClipboardText(s, part)
    if (text) {
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <span ref={chipRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] ${multi ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'} relative`}>
      <span className="text-gray-700">{s.name.slice(0, 24)}{s.name.length > 24 ? '…' : ''}</span>
      <span className="font-mono text-gray-500">{uc}</span>
      {pp != null && (
        <span className={`font-semibold cursor-help ${ppColor}`}>
          {pp.toFixed(2)}{pp > 1.5 ? ' ⚠️' : pp < 0.7 ? ' ✓' : ''}
        </span>
      )}
      {hasTip && showTip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          onClick={handleCopy}
          onMouseEnter={handleTipEnter}
          onMouseLeave={handleTipLeave}
          style={tipStyle}
          className="min-w-[420px] max-w-[680px] w-max bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:border-gray-300 transition-colors overflow-x-auto"
        >
          <PPTooltip s={s} part={part} />
          <div className="mt-2 pt-1.5 border-t border-gray-100 text-[9px] text-gray-400 text-center">
            {copied ? '✓ Copied to clipboard' : 'Click to copy'}
          </div>
        </div>,
        document.body
      )}
    </span>
  )
}

function ActionPlan({ opp, onPartClick }) {
  const steps = generateActionPlan(opp)
  const expansions = React.useMemo(() => generateExpandedStepData(opp), [opp])
  const [expanded, setExpanded] = React.useState({})
  const [generatingPDF, setGeneratingPDF] = React.useState(false)
  const [generatingRFQ, setGeneratingRFQ] = React.useState(false)
  // Build part number → file lookup for clickable PNs (includes overpriced + competitive items)
  const partMap = React.useMemo(() => {
    const m = {}
    ;(opp.parts || []).forEach(p => {
      if (p.file_id || p.filename) m[p.pn] = { fileId: p.file_id || null, filename: p.filename || '' }
    })
    ;(opp.vizData?.goodItems || []).forEach(g => {
      if (!m[g.pn] && (g.file_id || g.filename)) m[g.pn] = { fileId: g.file_id || null, filename: g.filename || '' }
    })
    return m
  }, [opp.parts, opp.vizData])
  if (!steps.length) return null

  const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))

  return (
    <div className="mt-4 bg-[#f0fdf4] border border-green-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 12l2 2 4-4" /></svg>
        Action Plan
        {expansions.length > 0 && <span className="text-[10px] text-gray-400 font-normal ml-2">Click steps to expand</span>}
      </div>
      <ol className="space-y-1 list-none pl-0">
        {steps.map((s, i) => {
          const exp = expansions[i] || null
          const isOpen = expanded[i]
          return (
            <li key={i} className="border border-gray-100 rounded-md overflow-hidden">
              <div
                className={`flex items-start gap-2 px-3 py-2 ${exp ? 'cursor-pointer hover:bg-green-50' : ''} transition-colors`}
                onClick={() => exp && toggle(i)}
              >
                <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-4 flex-shrink-0">{i + 1}.</span>
                {exp && (
                  <svg className={`w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                )}
                <span className="opp-action-step text-xs text-gray-700 leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: s }} />
              </div>
              {isOpen && exp && <StepExpansion data={exp} partMap={partMap} onPartClick={onPartClick} />}
            </li>
          )
        })}
      </ol>
      <div className="mt-3 pt-3 border-t border-green-200 flex items-center gap-2 flex-wrap">
        <span className="text-xs">{'\uD83C\uDFAF'}</span>
        <span className="text-xs text-gray-500">Target outcome:</span>
        <span className="text-sm font-bold text-green-700">{formatRange(opp.estSavings)}</span>
        <span className="text-[11px] text-gray-400">annual savings (conservative)</span>
      </div>
      <button
        disabled={generatingPDF}
        onClick={(e) => {
          e.stopPropagation()
          if (opp.type === 'negotiation') {
            downloadPlaybookPDF(opp, setGeneratingPDF)
          } else {
            generateDataPack(opp)
          }
        }}
        className={`mt-3 inline-flex items-center gap-2 px-4 py-2 text-white text-xs font-medium rounded-lg transition-colors ${
          generatingPDF ? 'bg-gray-400 cursor-wait' : 'bg-[#1e5045] hover:bg-[#0d5d57]'
        }`}
      >
        {generatingPDF ? (
          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
        )}
        {generatingPDF ? 'Generating Playbook…' : (opp.type === 'negotiation' ? 'Download PDF Playbook' : 'Generate Data Pack')}
      </button>
      {opp.type === 'negotiation' && (
        <button
          disabled={generatingRFQ}
          onClick={(e) => {
            e.stopPropagation()
            downloadRFQPackage(opp, setGeneratingRFQ)
          }}
          className={`mt-3 ml-2 inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border transition-colors ${
            generatingRFQ ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-wait' : 'bg-white text-[#1e5045] border-[#1e5045] hover:bg-[#f6f4ed]'
          }`}
        >
          {generatingRFQ ? (
            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
          )}
          {generatingRFQ ? 'Generating RFQ…' : 'Generate RFQ Package'}
        </button>
      )}
    </div>
  )
}

function StepExpansion({ data, partMap, onPartClick }) {
  // Render a cell value — if it matches a part number in partMap, make it clickable
  const renderCell = (cell, ci, headers) => {
    const hdr = (headers[ci] || '').toLowerCase()
    const isPnCol = hdr.includes('part')
    if (isPnCol && typeof cell === 'string' && partMap && partMap[cell] && onPartClick) {
      return (
        <td key={ci} className="px-2 py-1 font-mono whitespace-nowrap">
          <button onClick={(e) => { e.stopPropagation(); onPartClick(partMap[cell]) }} className="text-[#1e5045] hover:underline cursor-pointer font-semibold">{cell}</button>
        </td>
      )
    }
    return <td key={ci} className="px-2 py-1 text-gray-700 font-mono whitespace-nowrap">{cell}</td>
  }

  return (
    <div className="px-4 pb-3 pt-1 bg-white border-t border-gray-100">
      {/* Bullet points */}
      {data.bullets && data.bullets.length > 0 && (
        <ul className="text-[11px] text-gray-600 space-y-1 mb-2 list-disc pl-4">
          {data.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {/* Tables */}
      {data.tables && data.tables.map((t, ti) => (
        <div key={ti} className="mb-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.title}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] border border-gray-200 rounded">
              <thead>
                <tr className="bg-gray-50 text-gray-500 border-b border-gray-200">
                  {t.headers.map((h, hi) => <th key={hi} className="px-2 py-1 text-left font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-gray-50 hover:bg-gray-50">
                    {row.map((cell, ci) => renderCell(cell, ci, t.headers))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {/* Email draft */}
      {data.emailDraft && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Draft Email</div>
          <pre className="text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-[300px] overflow-y-auto">{data.emailDraft}</pre>
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(data.emailDraft) }}
            className="mt-1 text-[10px] text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
          >Copy to clipboard</button>
        </div>
      )}
    </div>
  )
}

function ExpandedDetail({ opp, onPartClick }) {
  // Sort parts to match the chart order: part_family sorts by lowest priced UC ascending
  const sortedParts = React.useMemo(() => {
    if (opp.type === 'part_family') {
      // Match vizData.partPrices order (by unit cost ascending)
      const priceMap = {}
      ;(opp.vizData?.partPrices || []).forEach((p, i) => { priceMap[p.pn] = i })
      return [...opp.parts].sort((a, b) => {
        const ai = priceMap[a.pn] ?? 9999
        const bi = priceMap[b.pn] ?? 9999
        return ai - bi
      })
    }
    return opp.parts
  }, [opp])

  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
      <div className="text-xs text-gray-600 leading-relaxed mb-3 bg-white border border-gray-200 rounded-md px-3 py-2">{opp.detail}</div>
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3"><OppViz opp={opp} onPartClick={onPartClick} /></div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-200">
              <th className="py-1.5 pr-3 font-medium">Part #</th>
              <th className="py-1.5 pr-3 font-medium">Description</th>
              <th className="py-1.5 pr-3 font-medium">Suppliers</th>
              <th className="py-1.5 font-medium text-right">Spend</th>
            </tr>
          </thead>
          <tbody>
            {sortedParts.slice(0, 25).map(p => (
              <tr key={p.pn} className="border-b border-gray-100 align-top">
                <td className="py-1.5 pr-3 font-mono text-gray-700">
                  <button onClick={() => onPartClick({ fileId: p.file_id || null, filename: p.filename })} className="text-[#1e5045] hover:underline cursor-pointer text-left">{p.pn}</button>
                </td>
                <td className="py-1.5 pr-3 text-gray-500">
                  <button onClick={() => onPartClick({ fileId: p.file_id || null, filename: p.filename })} className="text-gray-500 hover:text-[#1e5045] hover:underline cursor-pointer text-left">{p.description || '—'}</button>
                </td>
                <td className="py-1.5 pr-3"><div className="flex flex-wrap gap-1">{(p.suppliers || []).map((s, i) => <SupplierChip key={i} s={s} multi={p.suppliers.length > 1} part={p} />)}</div></td>
                <td className="py-1.5 text-right font-mono text-gray-700">{formatEur(p.total_spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedParts.length > 25 && <div className="text-[10px] text-gray-400 mt-1">+{sortedParts.length - 25} more parts (see Excel export)</div>}
      </div>
      <ActionPlan opp={opp} onPartClick={onPartClick} />
    </div>
  )
}

const DEFAULT_PARAMS = {
  savingsMultiplier: 0.65,
  priceThreshold: 1.3,
  minSize: 2000,
  outlierThreshold: 3,
  similarMinAttrs: 4,
  negotiationTarget: 1.1,
}

function ConfigSlider({ label, help, value, onChange, min, max, step, format }) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-gray-700">{label}</span>
        <span className="text-[11px] font-mono text-gray-500">{format(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gray-200 accent-[#1e5045]"
        style={{ background: `linear-gradient(to right, #1e5045 0%, #1e5045 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)` }}
      />
      <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
      {help && <div className="text-[10px] text-gray-500 mt-1">{help}</div>}
    </div>
  )
}

function ConfigPanel({ params, setParams, parts = [] }) {
  const isDefault = JSON.stringify(params) === JSON.stringify(DEFAULT_PARAMS)
  const attrCoverage = React.useMemo(() => {
    const axes = [
      { key: 'method', label: 'Method' },
      { key: 'material', label: 'Material' },
      { key: 'size', label: 'Size' },
      { key: 'tolerance_band', label: 'Tolerance' },
      { key: 'complexity_tier', label: 'Complexity' },
    ]
    const isUnknown = v => !v || ['unknown','unassigned','n/a','none'].includes(String(v).toLowerCase().trim()) || String(v).toLowerCase().trim().endsWith('unspec')
    return axes.map(a => ({ ...a, count: parts.filter(p => !isUnknown(p[a.key])).length }))
  }, [parts])
  return (
    <details className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-gray-700 select-none flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.09a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.09a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        Opportunity Settings
        {!isDefault && <span className="ml-2 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-normal">Modified</span>}
      </summary>
      <div className="px-5 pb-5 pt-2">
        <div className="flex flex-wrap gap-x-8 gap-y-5">
          <ConfigSlider
            label="Savings Estimate"
            help="How much of the modelled savings to show"
            value={params.savingsMultiplier}
            onChange={v => setParams(p => ({ ...p, savingsMultiplier: v }))}
            min={0.3} max={0.9} step={0.05}
            format={v => `${Math.round(v * 100)}% of model`}
          />
          <ConfigSlider
            label="Negotiation Target"
            help="Price level to negotiate toward (1.0 = benchmark, 1.1 = 10% premium)"
            value={params.negotiationTarget}
            onChange={v => setParams(p => ({ ...p, negotiationTarget: v }))}
            min={1.0} max={2.5} step={0.1}
            format={v => `${v.toFixed(1)}× benchmark`}
          />
          <ConfigSlider
            label="Price Threshold"
            help="Min % above benchmark to flag as overpriced"
            value={params.priceThreshold}
            onChange={v => setParams(p => ({ ...p, priceThreshold: v }))}
            min={1.1} max={1.8} step={0.05}
            format={v => `>${Math.round((v - 1) * 100)}% above`}
          />
          <div>
            <ConfigSlider
              label="Similar-Part Matching"
              help="Shared attributes needed for cross-PN comparison"
              value={params.similarMinAttrs}
              onChange={v => setParams(p => ({ ...p, similarMinAttrs: v }))}
              min={2} max={5} step={1}
              format={v => `${v} attributes`}
            />
            {parts.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
                {attrCoverage.map(a => (
                  <span key={a.key}>{a.label} <span className="font-mono text-gray-500">({a.count})</span></span>
                ))}
              </div>
            )}
          </div>
          <ConfigSlider
            label="Data Quality — Outlier Threshold"
            help="Price ratio above which to flag as likely error"
            value={params.outlierThreshold}
            onChange={v => setParams(p => ({ ...p, outlierThreshold: v }))}
            min={2} max={10} step={0.5}
            format={v => `>${v.toFixed(1)}x = warning`}
          />
          <ConfigSlider
            label="Minimum Opportunity Size"
            value={params.minSize}
            onChange={v => setParams(p => ({ ...p, minSize: v }))}
            min={500} max={50000} step={500}
            format={v => formatEur(v)}
          />
        </div>
        {!isDefault && (
          <button
            onClick={() => setParams(DEFAULT_PARAMS)}
            className="mt-4 text-[11px] text-gray-500 hover:text-gray-700 underline"
          >
            Reset to defaults
          </button>
        )}
      </div>
    </details>
  )
}

export default function OpportunitiesView({ parts, stats }) {
  const [expanded, setExpanded] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [selectedPart, setSelectedPart] = useState(null) // { fileId, filename }
  const [showTutorial, setShowTutorial] = useState(false)
  const [params, setParams] = useState(DEFAULT_PARAMS)

  const enrichedParts = useMemo(() => computePricePositions(parts), [parts])
  const opportunities = useMemo(() => {
    const opps = computeOpportunities(enrichedParts, params)
    opps.forEach(o => { o._negotiationTarget = params.negotiationTarget })
    return opps
  }, [enrichedParts, params])

  const totalSavings = deduplicatedSavings(opportunities)
  const highAch = opportunities.filter(o => (o.achievability?.score || 0) >= 80)
  const highAchSavings = deduplicatedSavings(highAch)
  const totalSpend = stats?.total_spend || 0

  const uniquePNs = new Set()
  opportunities.forEach(o => o.parts.forEach(p => uniquePNs.add(p.pn)))
  const totalExposed = enrichedParts.filter(p => uniquePNs.has(p.pn)).reduce((s, p) => s + (p.total_spend || 0), 0)

  const byType = {}
  opportunities.forEach(o => {
    if (!byType[o.type]) byType[o.type] = { count: 0, savings: 0 }
    byType[o.type].count++
    byType[o.type].savings += o.estSavings
  })
  const rawTotal = opportunities.reduce((s, o) => s + o.estSavings, 0)
  const donutSegments = Object.entries(byType).map(([type, d]) => ({
    type, savings: d.savings, pct: rawTotal > 0 ? d.savings / rawTotal * 100 : 0,
    label: OPP_TYPES[type]?.label || type, color: OPP_TYPES[type]?.color || '#94a3b8',
  }))

  const achHigh = opportunities.filter(o => o.achievability?.color === 'green').length
  const achMed = opportunities.filter(o => o.achievability?.color === 'amber').length
  const achLow = opportunities.filter(o => o.achievability?.color === 'red').length
  const achExplore = opportunities.filter(o => o.achievability?.color === 'gray').length
  const achTotal = opportunities.length || 1

  // Savings display uses the configured multiplier
  const fmtSavings = (raw) => formatRange(raw, params.savingsMultiplier)

  const handleExport = async () => {
    setExporting(true)
    try { await exportOpportunitiesXlsx(opportunities) } finally { setExporting(false) }
  }

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg mb-2">No opportunities detected</p>
        <p className="text-sm">Upload purchase history with pricing (and a Site column) to enable savings analysis</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <style>{STEP_STYLE}</style>

      <ConfigPanel params={params} setParams={setParams} parts={enrichedParts} />

      {/* Executive summary hero */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex gap-8 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Savings Opportunities</h2>
            <p className="text-xs text-gray-500 mb-5 max-w-xl">
              Algorithmically identified from cross-site price comparison, benchmarking, supply risk, and sourcing structure
              across <strong>{stats?.total_parts || 0} parts</strong> and <strong>{formatEur(totalSpend)}</strong> total addressable spend.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-lg font-bold text-green-600">{fmtSavings(totalSavings)}</div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Potential Annual Savings</div>
                <div className="text-[10px] text-gray-400">{opportunities.length} opportunities identified</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-lg font-bold text-green-600">{fmtSavings(highAchSavings)}</div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">High Achievability</div>
                <div className="text-[10px] text-gray-400">{highAch.length} ready to execute</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-lg font-bold text-[#1e5045]">{formatEur(totalExposed)}</div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase mt-1">Spend Under Review</div>
                <div className="text-[10px] text-gray-400">{totalSpend > 0 ? Math.round(totalExposed / totalSpend * 100) : 0}% of addressable spend</div>
              </div>
            </div>
          </div>

          <div className="w-72 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Donut segments={donutSegments} count={opportunities.length} />
              <div className="flex-1 space-y-1">
                {[...donutSegments].sort((a, b) => b.savings - a.savings).map(seg => (
                  <div key={seg.type} className="flex items-center gap-1.5 text-[11px]">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: seg.color }} />
                    <span className="text-gray-600 flex-1 truncate">{seg.label}</span>
                    <span className="font-medium text-gray-700">{fmtSavings(seg.savings)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] text-gray-400 uppercase mb-1">Achievability distribution</div>
              <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                <div className="bg-green-500" style={{ width: `${achHigh / achTotal * 100}%` }} />
                <div className="bg-amber-500" style={{ width: `${achMed / achTotal * 100}%` }} />
                <div className="bg-red-500" style={{ width: `${achLow / achTotal * 100}%` }} />
                <div className="bg-gray-400" style={{ width: `${achExplore / achTotal * 100}%` }} />
              </div>
              <div className="flex gap-3 mt-1 text-[10px]">
                <span className="text-green-600">High: {achHigh}</span>
                <span className="text-amber-600">Med: {achMed}</span>
                <span className="text-red-600">Low: {achLow}</span>
                {achExplore > 0 && <span className="text-gray-500">Explore: {achExplore}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <MethodologyPanel />

      {/* Opportunities table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">All Opportunities <span className="ml-1 text-xs text-gray-400">{opportunities.length}</span></h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTutorial(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              How to use
            </button>
            <button onClick={handleExport} disabled={exporting}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1e5045] text-white text-xs font-medium rounded-lg hover:bg-[#0d5d57] transition-colors disabled:opacity-50">
              <FaDownload size={11} />{exporting ? 'Exporting…' : 'Export Excel'}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-gray-400 uppercase border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">Opportunity</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium text-right">Parts</th>
                <th className="px-4 py-2.5 font-medium text-right">Spend Exposed</th>
                <th className="px-4 py-2.5 font-medium text-right">Potential Savings</th>
                <th className="px-4 py-2.5 font-medium">Achievability</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map((o, idx) => {
                const t = OPP_TYPES[o.type]
                const isOpen = expanded === idx
                return (
                  <React.Fragment key={idx}>
                    <tr className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${isOpen ? 'bg-gray-50' : ''}`} onClick={() => setExpanded(isOpen ? null : idx)}>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <FaChevronRight size={10} className={`mt-1 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <div>
                            <span className="text-gray-800 font-medium">{o.title}</span>
                            {(() => { const folders = [...new Set(o.parts.map(p => p.folder_name).filter(Boolean))]; return folders.length > 0 ? <div className="flex flex-wrap gap-1 mt-1">{folders.map(f => <span key={f} className="inline-block px-1.5 py-0.5 text-[9px] font-medium bg-gray-100 text-gray-500 rounded">{f}</span>)}</div> : null })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: t.bg, color: t.color }}>{t.icon} {t.label}</span></td>
                      <td className="px-4 py-3 text-right text-gray-600">{o.parts.length}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700">{formatEur(o.spendExposed)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-green-600">{fmtSavings(o.estSavings)}</td>
                      <td className="px-4 py-3"><AchievabilityBadge achievability={o.achievability} /></td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} className="p-0"><ExpandedDetail opp={o} onPartClick={setSelectedPart} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Part Detail Slide-Out */}
      {selectedPart && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setSelectedPart(null)} />
          <AtlasPartDetailPanel
            fileId={selectedPart.fileId}
            filename={selectedPart.filename}
            onClose={() => setSelectedPart(null)}
            onNavigate={(newId) => setSelectedPart({ fileId: newId, filename: null })}
          />
        </>
      )}

      {/* Tutorial Overlay */}
      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </div>
  )
}
